import React, { useState, useEffect, useMemo } from "react";
import { CalendarDays, CheckCircle2, Circle, Plus, Trash2, Heart, Clock, StickyNote } from "lucide-react";
import { useMember } from "../context/MemberContext";
import { fetchToolData, saveToolData } from "../lib/toolCloud";

const STORAGE_KEY = "dugun_ajandam_v1";

const DEFAULT_TASKS = [
  { title: "Düğün bütçesini belirle", monthsBefore: 12 },
  { title: "Düğün tarihini kesinleştir", monthsBefore: 12 },
  { title: "Düğün mekanını araştır ve gez", monthsBefore: 11 },
  { title: "Mekanı ayırt / kapora ver", monthsBefore: 10 },
  { title: "Davetli listesi taslağını çıkar", monthsBefore: 9 },
  { title: "Fotoğrafçı ve videografı belirle", monthsBefore: 8 },
  { title: "Organizasyon firması ile anlaş", monthsBefore: 7 },
  { title: "Gelinlik ve damatlık seçimi", monthsBefore: 6 },
  { title: "Müzik / DJ / orkestra ayarla", monthsBefore: 5 },
  { title: "Davetiye tasarımı ve baskısı", monthsBefore: 4 },
  { title: "Pasta ve ikram menüsünü seç", monthsBefore: 4 },
  { title: "Davetiyeleri gönder", monthsBefore: 3 },
  { title: "Alyansları seç", monthsBefore: 3 },
  { title: "Balayı planını yap", monthsBefore: 2 },
  { title: "Kuaför & makyaj denemesi", monthsBefore: 1 },
  { title: "Prova ve son onaylar", monthsBefore: 1 },
  { title: "Kalan ödemeleri tamamla", monthsBefore: 0 },
];

const uid = () => Math.random().toString(36).slice(2, 9);
const freshTasks = () => DEFAULT_TASKS.map((t) => ({ id: uid(), ...t, done: false }));
const defaultWeddingDate = () => new Date(Date.now() + 180 * 86400000).toISOString().slice(0, 10);

const AgendaTool = () => {
  const { token, member } = useMember();
  const mid = member?.id;
  const [weddingDate, setWeddingDate] = useState(defaultWeddingDate);
  const [tasks, setTasks] = useState(freshTasks);
  const [newTitle, setNewTitle] = useState("");
  const [newMonths, setNewMonths] = useState(1);
  const [openNote, setOpenNote] = useState(null);
  const [cloudReady, setCloudReady] = useState(false);

  useEffect(() => {
    let active = true;
    setCloudReady(false);
    (async () => {
      let d = await fetchToolData("ajandam", token);
      if (!d && mid) {
        try { const raw = localStorage.getItem(`${STORAGE_KEY}_${mid}`); if (raw) d = JSON.parse(raw); } catch {}
      }
      if (active) {
        setWeddingDate(d?.weddingDate || defaultWeddingDate());
        setTasks(Array.isArray(d?.tasks) && d.tasks.length ? d.tasks : freshTasks());
        setCloudReady(true);
      }
    })();
    return () => { active = false; };
  }, [token, mid]);

  useEffect(() => {
    if (!cloudReady || !mid) return;
    localStorage.setItem(`${STORAGE_KEY}_${mid}`, JSON.stringify({ weddingDate, tasks }));
    if (token) saveToolData("ajandam", token, { weddingDate, tasks });
  }, [weddingDate, tasks, cloudReady, token, mid]);

  const daysLeft = useMemo(() => {
    const d = new Date(weddingDate + "T00:00:00");
    const diff = Math.ceil((d - new Date(new Date().toDateString())) / 86400000);
    return diff;
  }, [weddingDate]);

  const dueDate = (monthsBefore) => {
    const d = new Date(weddingDate + "T00:00:00");
    d.setMonth(d.getMonth() - monthsBefore);
    const today = new Date(new Date().toDateString());
    return d < today ? today : d;
  };

  const sorted = useMemo(
    () => [...tasks].sort((a, b) => b.monthsBefore - a.monthsBefore),
    [tasks]
  );

  const doneCount = tasks.filter((t) => t.done).length;
  const progress = tasks.length ? Math.round((doneCount / tasks.length) * 100) : 0;

  const toggle = (id) => setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, done: !t.done } : t)));
  const remove = (id) => setTasks((ts) => ts.filter((t) => t.id !== id));
  const setNote = (id, note) => setTasks((ts) => ts.map((t) => (t.id === id ? { ...t, note } : t)));
  const add = (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setTasks((ts) => [...ts, { id: uid(), title: newTitle.trim(), monthsBefore: Number(newMonths) || 0, done: false }]);
    setNewTitle("");
    setNewMonths(1);
  };

  const fmt = (d) => d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  const isPast = (d) => d < new Date(new Date().toDateString());

  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 sm:p-8" data-testid="agenda-tool">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center">
          <CalendarDays className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold text-[#2A2224]">Düğün Ajandam</h1>
          <p className="text-sm text-[#7C6E70]">Tarih bazlı hazırlık listesi ve geri sayım</p>
        </div>
      </div>

      {/* Countdown + date */}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-4 items-center rounded-2xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] text-white p-6">
        <div className="flex items-center gap-4">
          <Heart className="w-10 h-10 text-white/90" fill="white" />
          <div>
            <p className="text-white/80 text-sm">Düğününüze</p>
            <p className="text-4xl font-bold leading-none">
              {daysLeft >= 0 ? `${daysLeft} gün` : "Geçti 🎉"}
            </p>
            {daysLeft >= 0 && <p className="text-white/80 text-sm mt-1">kaldı</p>}
          </div>
        </div>
        <div>
          <label className="block text-xs text-white/80 mb-1">Düğün tarihi</label>
          <input
            data-testid="agenda-date-input"
            type="date"
            value={weddingDate}
            onChange={(e) => setWeddingDate(e.target.value)}
            className="px-3 py-2 rounded-lg text-[#2A2224] text-sm outline-none"
          />
        </div>
      </div>

      {/* Progress */}
      <div className="mt-6">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-[#5b4d4f] font-medium">İlerleme</span>
          <span className="text-[#9E2A3B] font-semibold" data-testid="agenda-progress">{doneCount}/{tasks.length} · %{progress}</span>
        </div>
        <div className="h-2.5 rounded-full bg-[#F6E4E6] overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B] transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Add task */}
      <form onSubmit={add} className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_140px_auto] gap-3">
        <input
          data-testid="agenda-new-title"
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Yeni görev ekle..."
          className="px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
        />
        <div className="relative">
          <input
            data-testid="agenda-new-months"
            type="number"
            min="0"
            value={newMonths}
            onChange={(e) => setNewMonths(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#9c8f90] pointer-events-none">ay önce</span>
        </div>
        <button data-testid="agenda-add-btn" type="submit" className="flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors">
          <Plus className="w-4 h-4" /> Ekle
        </button>
      </form>

      {/* Task list */}
      <ul className="mt-5 divide-y divide-[#F6E4E6]" data-testid="agenda-list">
        {sorted.map((t) => {
          const d = dueDate(t.monthsBefore);
          return (
            <li key={t.id} className="flex flex-wrap items-center gap-3 py-3 group">
              <button onClick={() => toggle(t.id)} data-testid={`agenda-toggle-${t.id}`} className="shrink-0">
                {t.done
                  ? <CheckCircle2 className="w-6 h-6 text-[#9E2A3B]" />
                  : <Circle className="w-6 h-6 text-[#D9C4C7] hover:text-[#C15B6B] transition-colors" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium ${t.done ? "line-through text-[#b6a9ab]" : "text-[#2A2224]"}`}>{t.title}</p>
                <p className={`text-xs flex items-center gap-1 mt-0.5 ${!t.done && isPast(d) ? "text-[#C15B6B] font-medium" : "text-[#9c8f90]"}`}>
                  <Clock className="w-3 h-3" /> {fmt(d)}
                  {!t.done && isPast(d) && <span className="ml-1">· zamanı geçti</span>}
                </p>
              </div>
              <button
                onClick={() => setOpenNote(openNote === t.id ? null : t.id)}
                data-testid={`agenda-note-btn-${t.id}`}
                title="Not ekle"
                className={`shrink-0 transition-colors ${t.note ? "text-[#C6A15B]" : "text-[#c9a0a6] opacity-0 group-hover:opacity-100"} hover:text-[#9E2A3B]`}
              >
                <StickyNote className="w-4 h-4" fill={t.note ? "currentColor" : "none"} />
              </button>
              <button onClick={() => remove(t.id)} data-testid={`agenda-remove-${t.id}`} className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-[#c9a0a6] hover:text-[#9E2A3B]">
                <Trash2 className="w-4 h-4" />
              </button>

              {openNote === t.id ? (
                <div className="w-full pl-9">
                  <textarea
                    value={t.note || ""}
                    onChange={(e) => setNote(t.id, e.target.value)}
                    onBlur={() => setOpenNote(null)}
                    autoFocus
                    rows={2}
                    placeholder="Bu görev için not ekleyin..."
                    data-testid={`agenda-note-input-${t.id}`}
                    className="w-full px-3 py-2 rounded-lg border border-[#F0DDE0] bg-[#FBF5F0] text-sm text-[#2A2224] focus:outline-none focus:border-[#9E2A3B] resize-y"
                  />
                </div>
              ) : t.note ? (
                <button
                  onClick={() => setOpenNote(t.id)}
                  data-testid={`agenda-note-text-${t.id}`}
                  className="w-full pl-9 text-left"
                >
                  <p className="text-xs text-[#7C6E70] bg-[#FBF3E4] border border-[#EBD9AF] rounded-lg px-3 py-2 flex items-start gap-1.5">
                    <StickyNote className="w-3.5 h-3.5 text-[#C6A15B] mt-0.5 shrink-0" /> {t.note}
                  </p>
                </button>
              ) : null}
            </li>
          );
        })}
        {sorted.length === 0 && <li className="py-6 text-center text-sm text-[#9c8f90]">Henüz görev yok. Yukarıdan ekleyin.</li>}
      </ul>
    </div>
  );
};

export default AgendaTool;
